import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";
import { canInstall, isStandalone, onInstallableChange, triggerInstall } from "@/pwa";

const DISMISS_KEY = "mavinty_install_banner_dismissed_until";

function isIosSafari(): boolean {
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
}

function readDismissed(): number {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

export function InstallBanner() {
  const [installable, setInstallable] = useState(canInstall());
  const [dismissedUntil, setDismissedUntil] = useState<number>(() => readDismissed());
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const off = onInstallableChange(setInstallable);
    // Only show iOS Safari hint when beforeinstallprompt will never fire.
    if (typeof window !== "undefined" && isIosSafari() && !isStandalone()) {
      setIosHint(true);
    }
    return off;
  }, []);

  const now = Date.now();
  const hidden =
    isStandalone() || now < dismissedUntil || (!installable && !iosHint);

  if (hidden) return null;

  const dismiss = (days: number) => {
    const until = Date.now() + days * 24 * 60 * 60 * 1000;
    localStorage.setItem(DISMISS_KEY, String(until));
    setDismissedUntil(until);
  };

  const handleInstall = async () => {
    const outcome = await triggerInstall();
    if (outcome === "dismissed") {
      dismiss(3);
    }
  };

  // Placement: bottom-right desktop, above BottomNav on mobile.
  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-20 z-40 flex justify-center px-4 sm:bottom-6 sm:right-6 sm:justify-end">
      <div className="pointer-events-auto relative w-full max-w-sm rounded-xl bg-navy p-4 text-cream-light shadow-soft">
        <button
          type="button"
          onClick={() => dismiss(7)}
          aria-label="Скрыть"
          className="absolute right-2 top-2 text-cream-light hover:text-cream-light"
        >
          <X className="h-4 w-4" strokeWidth={1.8} />
        </button>
        <div className="flex items-start gap-3 pr-4">
          {installable ? (
            <Download className="mt-0.5 h-5 w-5 shrink-0 text-amber" strokeWidth={1.8} />
          ) : (
            <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-amber" strokeWidth={1.8} />
          )}
          <div className="flex-1 text-[13px] leading-snug">
            <div className="font-medium">Установить Mavinty</div>
            {installable ? (
              <p className="mt-1 text-[12px] text-cream-light">
                Добавь на домашний экран — откроется как приложение, без
                браузерной строки.
              </p>
            ) : (
              <p className="mt-1 text-[12px] text-cream-light">
                В Safari: нажми <strong>Поделиться</strong> → <strong>На экран «Домой»</strong>.
                Chrome / Firefox на iOS этого не умеют.
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {installable ? (
                <button
                  type="button"
                  onClick={handleInstall}
                  className="inline-flex items-center gap-1 rounded bg-amber px-2.5 py-1 text-[12px] font-medium text-navy hover:bg-amber-dark"
                >
                  Установить
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => dismiss(30)}
                className="inline-flex items-center rounded border border-cream-light/20 bg-cream-light/5 px-2.5 py-1 text-[11px] text-cream-light hover:bg-cream-light/10"
              >
                Позже
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
